import { getBearerHeaders } from "../helpers/GetBearerHeaders";

// Types for comment data
export interface CommentUser {
  id: string;
  username: string;
  avatar: string; // Currently bg_color, can be extended for real avatars
}

export interface Comment {
  id: string;
  userId: string;
  targetType: "debate" | "list" | "list_item";
  targetId: string;
  parentId: string | null;
  body: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  user: CommentUser;
  replies?: Comment[];
}

export interface CommentResponse {
  success: boolean;
  comments?: Comment[];
  comment?: Comment;
  message?: string;
}

/**
 * Fetch comments for a specific target
 */
export async function getComments(
  targetType: "debate" | "list" | "list_item",
  targetId: string,
): Promise<CommentResponse> {
  try {
    const { headers } = await getBearerHeaders();
    const response = await fetch(
      `/api/comments?targetType=${targetType}&targetId=${targetId}`,
      {
        method: "GET",
        headers: headers,
      },
    );

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching comments:", error);
    return {
      success: false,
      message: "Failed to fetch comments",
    };
  }
}

/**
 * Create a new comment
 */
export async function createComment(
  targetType: "debate" | "list" | "list_item",
  targetId: string,
  body: string,
  parentId?: string,
): Promise<CommentResponse> {
  try {
    const { headers } = await getBearerHeaders();
    const response = await fetch("/api/comments", {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        targetType,
        targetId,
        body,
        parentId,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating comment:", error);
    return {
      success: false,
      message: "Failed to create comment",
    };
  }
}

/**
 * Update an existing comment
 */
export async function updateComment(
  commentId: string,
  body: string,
): Promise<CommentResponse> {
  try {
    const { headers } = await getBearerHeaders();
    const response = await fetch(`/api/comments/${commentId}`, {
      method: "PATCH",
      headers: headers,
      body: JSON.stringify({
        body,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating comment:", error);
    return {
      success: false,
      message: "Failed to update comment",
    };
  }
}

/**
 * Delete a comment (soft delete)
 */
export async function deleteComment(
  commentId: string,
): Promise<CommentResponse> {
  try {
    const { headers } = await getBearerHeaders();
    const response = await fetch(`/api/comments/${commentId}`, {
      method: "DELETE",
      headers: headers,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting comment:", error);
    return {
      success: false,
      message: "Failed to delete comment",
    };
  }
}
